import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { currentFiscalYear, fiscalYearMonths, formatFY } from "@/lib/fiscal";
import { KpiCardGrid } from "./kpi-card-grid";
import { MetricTrendCharts } from "./metric-trend-charts";
import { MonthlyMetricsTable } from "./monthly-metrics-table";
import { RecordBanner } from "./record-banner";
import {
  aggregateRows,
  computeYoyDeltas,
  type ExecMonthlyRow,
  type ExecutiveDashboardResponse,
  fyForMonth,
} from "./types";

const ALL_FY = "all" as const;
type FyFilter = number | typeof ALL_FY;

export function ExecutiveDashboardPage() {
  const [data, setData] = useState<ExecutiveDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFY, setSelectedFY] = useState<FyFilter | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/executive-dashboard");
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as ExecutiveDashboardResponse;
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const availableFYs = useMemo(() => {
    if (!data) return [];
    const set = new Set<number>();
    for (const r of data.rows) set.add(fyForMonth(r.month));
    return [...set].sort((a, b) => b - a);
  }, [data]);

  // Initialize selectedFY once data arrives.
  useEffect(() => {
    if (selectedFY !== null || availableFYs.length === 0) return;
    const cur = currentFiscalYear();
    setSelectedFY(availableFYs.includes(cur) ? cur : (availableFYs[0] ?? ALL_FY));
  }, [availableFYs, selectedFY]);

  const aggregates = useMemo(() => {
    if (!data || selectedFY === null) return null;

    const fyRows =
      selectedFY === ALL_FY
        ? data.rows
        : data.rows.filter((r) => fyForMonth(r.month) === selectedFY);
    const current = aggregateRows(fyRows);

    if (selectedFY === ALL_FY) {
      return { current, yoy: {} };
    }

    // Build prior FY equivalent: take the same months one year earlier. For the
    // in-progress current month inside the selected FY, swap in the prorated
    // prior-year row so the comparison stays apples-to-apples.
    const currentMonthYoy = data.currentMonthYoy;
    const priorRows = fyRows.map((r) => {
      if (currentMonthYoy && r.month === currentMonthYoy.month) {
        return currentMonthYoy.priorRow;
      }
      const [yStr, mStr] = r.month.split("-");
      const priorYm = `${Number(yStr) - 1}-${mStr}`;
      return data.rows.find((p) => p.month === priorYm) ?? null;
    });
    const validPrior = priorRows.filter((r): r is NonNullable<typeof r> => r !== null);
    if (validPrior.length === 0) return { current, yoy: {} };
    const prior = aggregateRows(validPrior);
    return { current, yoy: computeYoyDeltas(current, prior) };
  }, [data, selectedFY]);

  const chartSeries = useMemo(() => {
    if (!data || selectedFY === null) return null;
    if (selectedFY === ALL_FY) {
      return { currentRows: data.rows, priorRows: undefined, currentLabel: "Trend" };
    }
    const allMonths = fiscalYearMonths(selectedFY);
    // For the current FY, truncate to elapsed months so future months don't render as zeros.
    const now = new Date();
    const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const months =
      selectedFY === currentFiscalYear() ? allMonths.filter((m) => m <= currentYm) : allMonths;
    const byMonth = new Map(data.rows.map((r) => [r.month, r]));
    const zero = (ym: string): ExecMonthlyRow => ({
      month: ym,
      opportunities: 0,
      orders: 0,
      grossSales: 0,
      netSales: 0,
      gpmDollars: 0,
      avgOrderValue: 0,
      gpmNetOverGross: 0,
      gpmCostBased: 0,
      closeRate: 0,
    });
    const currentRows = months.map((ym) => byMonth.get(ym) ?? zero(ym));
    const priorRows = months.map((ym) => {
      const [yStr, mStr] = ym.split("-");
      const priorYm = `${Number(yStr) - 1}-${mStr}`;
      if (data.currentMonthYoy && ym === data.currentMonthYoy.month) {
        return data.currentMonthYoy.priorRow;
      }
      return byMonth.get(priorYm) ?? null;
    });
    return {
      currentRows,
      priorRows,
      currentLabel: formatFY(selectedFY),
      priorLabel: formatFY(selectedFY - 1),
    };
  }, [data, selectedFY]);

  const headerDescription = useMemo(() => {
    if (selectedFY === null) return undefined;
    if (selectedFY === ALL_FY) return "All available history";
    return formatFY(selectedFY);
  }, [selectedFY]);

  if (loading) {
    return (
      <Shell description={undefined}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell description={undefined}>
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error ?? "No data available."}
        </div>
      </Shell>
    );
  }

  if (data.rows.length === 0) {
    return (
      <Shell description={undefined}>
        <div className="rounded-md border border-muted bg-muted/30 p-6 text-sm text-muted-foreground">
          No deal data available. Make sure pipelines are synced and a Production pipeline exists.
        </div>
      </Shell>
    );
  }

  const currentRow = data.rows[data.rows.length - 1];

  return (
    <Shell
      description={headerDescription}
      actions={
        <FyFilter
          availableFYs={availableFYs}
          selectedFY={selectedFY ?? ALL_FY}
          onChange={setSelectedFY}
        />
      }
    >
      {data.newRecordsThisMonth.length > 0 && currentRow && (
        <RecordBanner recordKeys={data.newRecordsThisMonth} currentRow={currentRow} />
      )}

      {aggregates && (
        <KpiCardGrid
          currentRow={aggregates.current}
          yoy={aggregates.yoy}
          recordKeys={selectedFY === currentFiscalYear() ? data.newRecordsThisMonth : []}
        />
      )}

      <Tabs defaultValue="table" className="space-y-4">
        <TabsList>
          <TabsTrigger value="table">Table</TabsTrigger>
          <TabsTrigger value="charts">Charts</TabsTrigger>
        </TabsList>
        <TabsContent value="table">
          <MonthlyMetricsTable
            rows={data.rows}
            yoyByMonth={data.yoyByMonth}
            selectedFY={selectedFY ?? ALL_FY}
          />
        </TabsContent>
        <TabsContent value="charts">
          {chartSeries && (
            <MetricTrendCharts
              currentRows={chartSeries.currentRows}
              priorRows={chartSeries.priorRows}
              currentLabel={chartSeries.currentLabel}
              priorLabel={chartSeries.priorLabel}
            />
          )}
        </TabsContent>
      </Tabs>
    </Shell>
  );
}

function FyFilter({
  availableFYs,
  selectedFY,
  onChange,
}: {
  availableFYs: number[];
  selectedFY: FyFilter;
  onChange: (fy: FyFilter) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {availableFYs.map((fy) => (
        <Button
          key={fy}
          type="button"
          size="sm"
          variant={selectedFY === fy ? "default" : "outline"}
          className={
            selectedFY === fy ? "bg-brand-gold text-brand-navy hover:bg-brand-gold/90" : undefined
          }
          onClick={() => onChange(fy)}
        >
          {formatFY(fy)}
        </Button>
      ))}
      <Button
        type="button"
        size="sm"
        variant={selectedFY === ALL_FY ? "default" : "outline"}
        className={
          selectedFY === ALL_FY ? "bg-brand-gold text-brand-navy hover:bg-brand-gold/90" : undefined
        }
        onClick={() => onChange(ALL_FY)}
      >
        All
      </Button>
    </div>
  );
}

function Shell({
  description,
  actions,
  children,
}: {
  description: string | undefined;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Executive Dashboard" description={description} actions={actions} />
      {children}
    </div>
  );
}
